// tool-executor — browser-side dispatch table for AI function_calls.
//
// Spec: docs/specs/ai-chat-guided-tour.md §10.2 + §11.1.
//
// Each tool call from the AI is executed in the browser with the
// user's own cookie auth — never elevated. RBAC + db-router scoping
// happen at the existing sub-API layer; this dispatcher is a thin
// router from (name + args) → existing /v1/* endpoint.
//
// If a tool errors out (network / 403 / etc.), we return an error
// payload INSTEAD of throwing — the AI then sees the failure as a
// tool_result and can recover (e.g., explain that the user lacks
// permission) rather than the whole chat blowing up.

import { apiFetch } from "../../lib/api-client.js";
import type { ScreenMetaRich } from "./ScreenMetaContext.js";
import { PERMISSION_CATALOG, DOMAIN_LABELS } from "../lib/permissionCatalog.js";
import { enrichLabels } from "@nexus/shared/labels";

export interface ToolCallRequest {
  call_id: string;
  name: string;
  args: Record<string, unknown>;
  /** Set true by ai-api when the model called a tool not in the
   *  registry (hallucination). Executor short-circuits with an error. */
  known?: boolean;
}

export interface ToolCallResult {
  call_id: string;
  /** JSON-serialisable payload that gets fed back to the AI via
   *  POST /v1/ai/threads/:id/tool-result. */
  output: unknown;
}

/** Browser-side dispatch dependencies — passed in so the executor can
 *  read the current ScreenMetaContext without leaking React internals. */
export interface ToolExecutorDeps {
  /** Snapshot Layer C-rich screen meta. */
  getScreenMetaRich: () => ScreenMetaRich;
}

export async function executeAiTool(
  call: ToolCallRequest,
  deps: ToolExecutorDeps,
): Promise<ToolCallResult> {
  if (call.known === false) {
    return errorResult(call, "unknown_tool", `Tool ${call.name} is not in the registry`);
  }

  try {
    switch (call.name) {
      case "get_me": {
        const r = await apiFetch("/v1/me", { credentials: "include" });
        if (!r.ok) return errorResult(call, "fetch_failed", `HTTP ${r.status}`, r.status);
        return { call_id: call.call_id, output: await r.json() };
      }

      case "list_sites": {
        const qs = buildQuery({
          status: pickString(call.args, "status"),
          q: pickString(call.args, "q"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/sites${qs}`);
      }

      case "list_locations": {
        const qs = buildQuery({
          site_id: pickString(call.args, "site_id"),
          status: pickString(call.args, "status"),
          risk: pickString(call.args, "risk"),
          pairing: pickString(call.args, "pairing"),
          q: pickString(call.args, "q"),
          limit: pickInt(call.args, "limit", 100),
        });
        return await fetchAndWrap(call, `/v1/locations${qs}`);
      }

      case "list_routes": {
        const qs = buildQuery({
          site_id: pickString(call.args, "site_id"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/routes${qs}`);
      }

      case "list_tasks": {
        const qs = buildQuery({ limit: pickInt(call.args, "limit", 50) });
        return await fetchAndWrap(call, `/v1/admin/tasks${qs}`);
      }

      case "list_users": {
        const qs = buildQuery({
          role: pickString(call.args, "role"),
          status: pickString(call.args, "status"),
          q: pickString(call.args, "q"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/admin/users${qs}`);
      }

      case "list_roles": {
        // P3 backend lists both system + tenant-custom roles. include_archived
        // arg is reserved for future filter — server currently returns all
        // active rows by default; archived ones surface via dedicated endpoint
        // when needed.
        return await fetchAndWrap(call, `/v1/admin/roles`);
      }

      case "list_permissions": {
        // Permission catalog is hand-curated frontend-side (mirrors migrations
        // 0037/0038/0039) — no server fetch needed. Filter by domain inline.
        const domain = pickString(call.args, "domain");
        const items = domain
          ? PERMISSION_CATALOG.filter((p) => p.domain === domain)
          : [...PERMISSION_CATALOG];
        return {
          call_id: call.call_id,
          output: {
            items: items.map((p) => ({
              id: p.id,
              domain: p.domain,
              category: p.category,
              action: p.action,
              label_zh: p.label_zh,
              description_zh: p.description_zh,
            })),
            domain_labels: DOMAIN_LABELS,
            total: items.length,
          },
        };
      }

      case "get_role_permissions": {
        const roleId = pickString(call.args, "role_id");
        if (!roleId) return errorResult(call, "missing_role_id", "role_id is required");
        return await fetchAndWrap(call, `/v1/admin/roles/${encodeURIComponent(roleId)}`);
      }

      case "list_user_roles": {
        const userId = pickString(call.args, "user_id");
        if (!userId) return errorResult(call, "missing_user_id", "user_id is required");
        return await fetchAndWrap(call, `/v1/admin/users/${encodeURIComponent(userId)}/roles`);
      }

      case "list_clients": {
        const qs = buildQuery({
          q: pickString(call.args, "q"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/clients${qs}`);
      }

      // Module-owned tool: list assets. Slice lives in
      // @nexus/module-asset/ai/tools.
      case "list_assets": {
        const qs = buildQuery({
          site_id: pickString(call.args, "site_id"),
          kind: pickString(call.args, "kind"),
          status: pickString(call.args, "status"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/assets${qs}`);
      }

      // Module-owned tool: update an asset (rename / retire / mark
      // serviced etc.). All fields optional except asset_id; we
      // forward whichever ones the model populated.
      case "update_asset": {
        const assetId = pickString(call.args, "asset_id");
        if (!assetId) {
          return errorResult(call, "missing_asset_id", "asset_id required", 400);
        }
        // Build sparse body — only include keys the model actually
        // set. Pre-trim strings: empty-string is a deliberate "clear"
        // signal in the API contract, so we pass it through as-is
        // instead of treating it as "not provided".
        const body: Record<string, unknown> = {};
        for (const key of ["name", "kind", "site_id", "status", "description", "serviced_at"] as const) {
          const v = call.args[key];
          if (typeof v === "string") body[key] = v;
        }
        const r = await apiFetch(`/v1/assets/${encodeURIComponent(assetId)}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          let bodyJson: unknown;
          try { bodyJson = await r.json(); } catch { bodyJson = null; }
          return errorResult(
            call,
            "update_failed",
            `HTTP ${r.status}${bodyJson ? `: ${JSON.stringify(bodyJson)}` : ""}`,
            r.status,
          );
        }
        return { call_id: call.call_id, output: enrichLabels(await r.json()) };
      }

      case "list_incidents": {
        const qs = buildQuery({
          site_id: pickString(call.args, "site_id"),
          severity: pickString(call.args, "severity"),
          status: pickString(call.args, "status"),
          from: pickString(call.args, "from"),
          to: pickString(call.args, "to"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/incidents${qs}`);
      }

      // Module-owned tool: incident escalate. Slice lives in
      // @nexus/module-incident/ai/tools. Dispatcher mirrors the
      // server-side route at POST /v1/incidents/:id/escalate; server
      // enforces severity-ordering + permission, so we just forward.
      case "escalate_incident": {
        const incidentId = pickString(call.args, "incident_id");
        const toSeverity = pickString(call.args, "to_severity");
        const reason = pickString(call.args, "reason");
        if (!incidentId || !toSeverity) {
          return errorResult(call, "missing_args", "incident_id and to_severity required", 400);
        }
        const r = await apiFetch(`/v1/incidents/${encodeURIComponent(incidentId)}/escalate`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ to_severity: toSeverity, reason: reason ?? undefined }),
        });
        if (!r.ok) {
          let bodyJson: unknown;
          try { bodyJson = await r.json(); } catch { bodyJson = null; }
          return errorResult(
            call,
            "escalate_failed",
            `HTTP ${r.status}${bodyJson ? `: ${JSON.stringify(bodyJson)}` : ""}`,
            r.status,
          );
        }
        return { call_id: call.call_id, output: enrichLabels(await r.json()) };
      }

      // Module-owned tool: incident assign. POST /v1/incidents/:id/assign
      // writes assigned_officer_id + assigned_officer_at columns (PR
      // #625) on the incidents row + emits incident.assigned with
      // previous_officer_id for hand-off notifications. Server validates
      // officer belongs to the same tenant.
      case "assign_incident": {
        const incidentId = pickString(call.args, "incident_id");
        const officerId = pickString(call.args, "officer_id");
        const reason = pickString(call.args, "reason");
        if (!incidentId || !officerId) {
          return errorResult(call, "missing_args", "incident_id and officer_id required", 400);
        }
        const r = await apiFetch(`/v1/incidents/${encodeURIComponent(incidentId)}/assign`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ officer_id: officerId, reason: reason ?? undefined }),
        });
        if (!r.ok) {
          let bodyJson: unknown;
          try { bodyJson = await r.json(); } catch { bodyJson = null; }
          return errorResult(
            call,
            "assign_failed",
            `HTTP ${r.status}${bodyJson ? `: ${JSON.stringify(bodyJson)}` : ""}`,
            r.status,
          );
        }
        return { call_id: call.call_id, output: enrichLabels(await r.json()) };
      }

      // Module-owned tool: incident timeline. GET /v1/incidents/:id/timeline
      // returns the chronological audit feed for one incident; server
      // writes its own incident.timeline_viewed audit row (sensitive
      // read, surfaces in tenant_admin's monthly review).
      case "view_incident_timeline": {
        const incidentId = pickString(call.args, "incident_id");
        if (!incidentId) {
          return errorResult(call, "missing_args", "incident_id required", 400);
        }
        return await fetchAndWrap(call, `/v1/incidents/${encodeURIComponent(incidentId)}/timeline`);
      }

      case "inspect_tenant_topology": {
        return await fetchAndWrap(call, `/v1/tenants/topology`);
      }

      case "list_task_assignments": {
        const taskId = pickString(call.args, "task_id");
        const locationId = pickString(call.args, "location_id");
        if (!taskId && !locationId) {
          return errorResult(call, "missing_filter", "Provide either task_id or location_id");
        }
        const path = taskId
          ? `/v1/admin/tasks/${encodeURIComponent(taskId)}/assignments`
          : `/v1/admin/locations/${encodeURIComponent(locationId!)}/assignments`;
        return await fetchAndWrap(call, path);
      }

      case "get_today_overview": {
        const tenantId = pickString(call.args, "tenant_id");
        const path = tenantId
          ? `/v1/patrols/manager/today?tenant_id=${encodeURIComponent(tenantId)}`
          : `/v1/patrols/manager/today`;
        return await fetchAndWrap(call, path);
      }

      case "list_officers": {
        const qs = buildQuery({
          status: pickString(call.args, "status"),
          q: pickString(call.args, "q"),
        });
        return await fetchAndWrap(call, `/v1/admin/officers${qs}`);
      }

      case "get_officer_enrollment": {
        const officerId = pickString(call.args, "officer_id");
        if (!officerId) {
          return errorResult(call, "missing_officer_id", "officer_id is required");
        }
        return await fetchAndWrap(call, `/v1/admin/officers/${encodeURIComponent(officerId)}/enroll-status`);
      }

      case "list_shifts": {
        const qs = buildQuery({
          status: pickString(call.args, "status"),
        });
        return await fetchAndWrap(call, `/v1/schedules/shifts${qs}`);
      }

      case "list_schedule_roster": {
        const qs = buildQuery({
          from: pickString(call.args, "from"),
          to: pickString(call.args, "to"),
          site_id: pickString(call.args, "site_id"),
          officer_id: pickString(call.args, "officer_id"),
          shift_id: pickString(call.args, "shift_id"),
        });
        return await fetchAndWrap(call, `/v1/schedules/roster${qs}`);
      }

      case "list_active_patrol_sessions": {
        const qs = buildQuery({
          site_id: pickString(call.args, "site_id"),
        });
        return await fetchAndWrap(call, `/v1/patrols/sessions/active${qs}`);
      }

      case "get_submission_detail": {
        const submissionId = pickString(call.args, "submission_id");
        if (!submissionId) {
          return errorResult(call, "missing_submission_id",
            "submission_id is required.");
        }
        return await fetchAndWrap(call, `/v1/patrols/submissions/${encodeURIComponent(submissionId)}`);
      }

      case "inspect_scan_session": {
        const sessionToken = pickString(call.args, "session_token");
        if (!sessionToken) {
          return errorResult(call, "missing_session_token",
            "session_token argument is required. Read it from screen_context if the user is on /scan/* pages, otherwise ask them to paste their scan URL.");
        }
        // Lazy-load the API module so the AI tool path doesn't pull
        // scanWizardPlanApi into the dashboard's main bundle for users
        // who never trigger this tool.
        const { fetchScanWizardPlan } = await import("../../lib/scanWizardPlanApi.js");
        const result = await fetchScanWizardPlan(sessionToken);
        switch (result.kind) {
          case "ok":
            return { call_id: call.call_id, output: result.plan };
          case "expired":
            return {
              call_id: call.call_id,
              output: { kind: "expired", hint: "Scan session has timed out (30-min TTL). Officer needs to re-tap the NTAG tag." },
            };
          case "not_modified":
            // We don't pass since_version, so this branch shouldn't fire,
            // but keep handling so a future caller change doesn't break.
            return { call_id: call.call_id, output: { kind: "not_modified" } };
          case "error":
            return errorResult(call, result.error, `wizard-plan fetch failed (HTTP ${result.status})`, result.status);
        }
      }

      case "list_notification_logs": {
        const qs = buildQuery({
          status: pickString(call.args, "status"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/notifications/logs${qs}`);
      }

      case "list_reports": {
        const qs = buildQuery({
          kind: pickString(call.args, "kind") ?? "daily",
        });
        return await fetchAndWrap(call, `/v1/reports${qs}`);
      }

      case "list_patrol_events": {
        const qs = buildQuery({
          from: pickString(call.args, "from"),
          to: pickString(call.args, "to"),
          site_id: pickString(call.args, "site_id"),
          location_id: pickString(call.args, "location_id"),
          officer_id: pickString(call.args, "officer_id"),
          cmac_verified: pickBool(call.args, "cmac_verified"),
          limit: pickInt(call.args, "limit", 50),
          cursor: pickString(call.args, "cursor"),
        });
        return await fetchAndWrap(call, `/v1/patrols/events${qs}`);
      }

      case "summarize_patrol_activity": {
        const groupBy = pickString(call.args, "group_by");
        if (!groupBy) {
          return errorResult(call, "missing_group_by", "group_by is required (location | site | officer)");
        }
        const qs = buildQuery({
          group_by: groupBy,
          from: pickString(call.args, "from"),
          to: pickString(call.args, "to"),
          site_id: pickString(call.args, "site_id"),
          officer_id: pickString(call.args, "officer_id"),
          limit: pickInt(call.args, "limit", 50),
        });
        return await fetchAndWrap(call, `/v1/patrols/summary${qs}`);
      }

      case "inspect_tenant_state": {
        // Aggregate counts across 6 list endpoints in parallel.
        // We ask each list for total only via limit=1; the response
        // payload includes `total` for these endpoints.
        const fields = ["sites", "locations", "routes", "tasks", "users"] as const;
        const responses = await Promise.all([
          countOf("/v1/sites?limit=1"),
          countOf("/v1/locations?limit=1"),
          countOf("/v1/routes?limit=1"),
          countOf("/v1/admin/tasks?limit=1"),
          countOf("/v1/admin/users?limit=1"),
        ]);
        const summary: Record<string, number | { error: string }> = {};
        for (let i = 0; i < fields.length; i++) {
          summary[fields[i]] = responses[i];
        }
        return { call_id: call.call_id, output: summary };
      }

      case "inspect_current_page": {
        const includeRows = call.args.include_rows === true;
        const includeFormState = call.args.include_form_state === true;
        const snap = deps.getScreenMetaRich();
        // Phase 1: include_rows / include_form_state are recognised but
        // we only return the registered data_summary today. PR #6 (tour
        // engine) extends this with data-tour-id-based row + form
        // introspection; until then, summary is the canonical surface.
        const out = {
          ...snap,
          // Strip rich fields the caller didn't ask for, for token frugality.
          data_summary: includeRows ? snap.data_summary : null,
          form_state: includeFormState ? null : null,
        };
        return { call_id: call.call_id, output: out };
      }

      // ─── super_admin cross-tenant tools (PR #5b) ──────────────────
      // Backend tags these as super_admin_only; ai-api stream pipeline
      // marks the SSE event known:false (with reason:"forbidden_role")
      // if a non-super_admin actor somehow gets a call for one. The
      // top-of-function `call.known === false` short-circuit handles
      // that, so by the time we reach these cases the actor is
      // super_admin and the endpoint will accept it.

      case "list_tenants": {
        const qs = buildQuery({
          include_demo: call.args.include_demo === true ? "1" : null,
        });
        return await fetchAndWrap(call, `/v1/platform/tenants${qs}`);
      }

      case "inspect_platform_state": {
        const qs = buildQuery({
          include_demo: call.args.include_demo === true ? "1" : null,
        });
        return await fetchAndWrap(call, `/v1/platform/state${qs}`);
      }

      case "inspect_tenant_state_for": {
        const tenantId = pickString(call.args, "tenant_id");
        if (!tenantId) {
          return errorResult(call, "missing_tenant_id", "tenant_id arg is required");
        }
        // Aggregate counts for ONE specific tenant by parameterising
        // each list endpoint with ?tenant_id=. Rbac.resolveTenant on
        // each sub-API accepts an explicit tenant_id from super_admin
        // without requiring impersonation. Failures show up per-field
        // (a tenant with no locations just returns 0; a permission
        // surprise shows up as `{error:'http_403'}` on that field
        // alone, not blowing up the whole tool call).
        const fields = ["sites", "locations", "routes", "tasks", "users"] as const;
        const responses = await Promise.all([
          countOf(`/v1/sites?tenant_id=${encodeURIComponent(tenantId)}&limit=1`),
          countOf(`/v1/locations?tenant_id=${encodeURIComponent(tenantId)}&limit=1`),
          countOf(`/v1/routes?tenant_id=${encodeURIComponent(tenantId)}&limit=1`),
          countOf(`/v1/admin/tasks?tenant_id=${encodeURIComponent(tenantId)}&limit=1`),
          countOf(`/v1/admin/users?tenant_id=${encodeURIComponent(tenantId)}&limit=1`),
        ]);
        const summary: Record<string, number | { error: string }> = {};
        for (let i = 0; i < fields.length; i++) summary[fields[i]] = responses[i];
        return { call_id: call.call_id, output: { tenant_id: tenantId, ...summary } };
      }

      case "suggest_shared_skill": {
        // The AI proposes a tenant-agnostic operational skill the
        // super_admin team should consider. We DELIBERATELY DO NOT
        // write the candidate from the browser — that path goes via
        // an internal cron / queue with anonymization (PR-S7.1).
        // Return a silent ack so the AI chain continues without
        // leaking the proposal back into the chat bubble for the
        // operator. Spec docs/specs/shared-skill-layer.md §5.
        // The AI's emitted args are logged server-side for the
        // future writer to pick up (visible via audit_log
        // `ai.tool_result` with output_length).
        return {
          call_id: call.call_id,
          output: {
            received: true,
            note: "skill suggestion queued for super_admin review (not user-visible)",
          },
        };
      }

      case "capture_satellite_map": {
        const body = {
          site_id: pickString(call.args, "site_id"),
          floor_id: pickString(call.args, "floor_id"),
          bbox: call.args?.bbox,
          zoom: pickInt(call.args, "zoom", 18),
          tile_source: pickString(call.args, "tile_source") ?? "nlsc_photo_mix",
        };
        const r = await apiFetch("/v1/map-captures", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({})) as Record<string, unknown>;
          return errorResult(call, (err.error as string) ?? "capture_failed", (err.hint as string) ?? `HTTP ${r.status}`, r.status);
        }
        return { call_id: call.call_id, output: await r.json() };
      }

      case "list_my_api_keys": {
        // No args — endpoint is implicitly scoped to the caller via cookie.
        return await fetchAndWrap(call, `/v1/me/api-keys`);
      }

      case "list_satellite_captures": {
        const qs = buildQuery({ site_id: pickString(call.args, "site_id") });
        return await fetchAndWrap(call, `/v1/map-captures${qs}`);
      }

      case "get_visual_layers": {
        const siteId = pickString(call.args, "site_id") ?? "";
        return await fetchAndWrap(call, `/v1/sites/${encodeURIComponent(siteId)}/visual-layers`);
      }

      case "trigger_visual_upgrade": {
        const siteId = pickString(call.args, "site_id") ?? "";
        const targetKind = pickString(call.args, "target_kind") ?? "";
        // Find the current layout for this site to get version + r2_key
        const layersR = await apiFetch(`/v1/sites/${encodeURIComponent(siteId)}/visual-layers`, { credentials: "include" });
        if (!layersR.ok) return errorResult(call, "fetch_failed", `HTTP ${layersR.status}`, layersR.status);
        const layers = await layersR.json() as { site_layers?: Array<{ source_kind: string; layout_id?: string }> };
        const currentLayer = (layers.site_layers ?? [])[0];
        if (!currentLayer) return errorResult(call, "no_visual_layer", "Site has no visual layer to upgrade");
        // POST to map-captures with a trigger_upgrade flag
        const r = await apiFetch("/v1/map-captures/upgrade", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            layout_id: currentLayer.layout_id,
            target_kind: targetKind,
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({})) as Record<string, unknown>;
          return errorResult(call, (err.error as string) ?? "upgrade_failed", `HTTP ${r.status}`, r.status);
        }
        return { call_id: call.call_id, output: await r.json() };
      }

      default:
        return errorResult(call, "unknown_tool", `No client handler for ${call.name}`);
    }
  } catch (err) {
    return errorResult(call, "executor_threw", err instanceof Error ? err.message : String(err));
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function errorResult(call: ToolCallRequest, code: string, message: string, status?: number): ToolCallResult {
  return {
    call_id: call.call_id,
    output: { error: code, message, ...(status !== undefined ? { status } : {}) },
  };
}

async function fetchAndWrap(call: ToolCallRequest, url: string): Promise<ToolCallResult> {
  const r = await apiFetch(url, { credentials: "include" });
  if (!r.ok) {
    let body: unknown;
    try { body = await r.json(); } catch { body = null; }
    return errorResult(call, "fetch_failed", `HTTP ${r.status}`, r.status);
  }
  // Walk the response and inject `<field>_zh` siblings for known
  // enum-bearing fields (role / status / target_kind / actor_kind etc.,
  // see packages/shared/src/labels). This is the structural fix for
  // the AI echoing raw `tenant_admin` / `active` strings to users:
  // the data itself now carries the display label, and the model uses
  // it naturally instead of needing a prompt rule per enum.
  return { call_id: call.call_id, output: enrichLabels(await r.json()) };
}

async function countOf(path: string): Promise<number | { error: string }> {
  try {
    const r = await apiFetch(path, { credentials: "include" });
    if (!r.ok) return { error: `http_${r.status}` };
    const body = (await r.json()) as { total?: number; items?: unknown[] };
    if (typeof body.total === "number") return body.total;
    if (Array.isArray(body.items)) return body.items.length;
    return 0;
  } catch (err) {
    return { error: err instanceof Error ? err.message : "fetch_threw" };
  }
}

function buildQuery(params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

function pickString(args: Record<string, unknown>, key: string): string | null {
  const v = args[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function pickBool(args: Record<string, unknown>, key: string): string | null {
  const v = args[key];
  if (v === true) return "true";
  if (v === false) return "false";
  return null;
}

function pickInt(args: Record<string, unknown>, key: string, fallback: number): number {
  const v = args[key];
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string") {
    const n = parseInt(v, 10);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}
